/* v3-product.js: <v3-product-form>, <v3-gallery>, <v3-sticky-bar>, <v3-countdown>, pairs-well-with.
   Native picker mode: option inputs (radios, selects, hidden) -> variant -> price, availability, URL, gallery.
   Single option products carry the variant id on the radios themselves (name="id"), so the picker works with
   no script; multi option products keep options[...] inputs and the hidden id input this script updates.
   App managed mode (VO Product Options): once the app's UI appears we read the variant id it writes into
   input[name="id"] (section level change/input/click listeners plus a MutationObserver on the section; no cached
   node references, no polling). Until then the native selects drive the form.
   Custom elements throughout so an editor re-render of a section wires the fresh markup and drops the old. */
(function () {
  'use strict';
  var doc = document;
  var v3 = window.v3 = window.v3 || {};
  var APP_SEL = '.bcpo, .vopo, [class*="bcpo-"], [class*="vopo-"]';
  var mobile = window.matchMedia('(max-width: 900px)');

  /* Shopify money formats: {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
     {{amount_no_decimals_with_comma_separator}}, {{amount_with_space_separator}}, {{amount_with_apostrophe_separator}} */
  function formatMoney(cents, format) {
    var f = (format || '£{{amount}}').replace(/<[^>]+>/g, '');
    var m = f.match(/\{\{\s*(\w+)\s*\}\}/); if (!m) return f;
    var n = cents / 100, out;
    function group(s, sep) { return s.replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
    switch (m[1]) {
      case 'amount_no_decimals': out = group(String(Math.round(n)), ','); break;
      case 'amount_with_comma_separator': out = group(n.toFixed(2).split('.')[0], '.') + ',' + n.toFixed(2).split('.')[1]; break;
      case 'amount_no_decimals_with_comma_separator': out = group(String(Math.round(n)), '.'); break;
      case 'amount_with_space_separator': out = group(n.toFixed(2).split('.')[0], ' ') + ',' + n.toFixed(2).split('.')[1]; break;
      case 'amount_with_apostrophe_separator': out = group(n.toFixed(2).split('.')[0], "'") + '.' + n.toFixed(2).split('.')[1]; break;
      default: out = group(n.toFixed(2).split('.')[0], ',') + '.' + n.toFixed(2).split('.')[1];
    }
    return f.replace(m[0], out);
  }
  function setText(el, text) { if (el && el.textContent !== text) el.textContent = text; } /* write only on change */
  function productForm() { return doc.querySelector('v3-product-form'); }

  /* ---------- <v3-gallery>: swipe track, thumbs, progress line ---------- */
  class V3Gallery extends HTMLElement {
    connectedCallback() {
      var self = this, main = this.main = this.querySelector('[data-v3-gallery-main]');
      if (!main) return;
      /* mouse drag: a short horizontal drag pages the carousel (touch and trackpad scroll natively) */
      var drag = null;
      main.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse' || e.button !== 0) return; drag = { x: e.clientX, left: main.scrollLeft, moved: false }; });
      main.addEventListener('pointermove', function (e) { if (!drag) return; var dx = e.clientX - drag.x; if (Math.abs(dx) > 4) { drag.moved = true; main.classList.add('is-dragging'); main.scrollLeft = drag.left - dx; } });
      var endDrag = function (e) {
        if (!drag) return; var dx = e.clientX - drag.x, d = drag; drag = null; main.classList.remove('is-dragging');
        if (!d.moved) return;
        var from = Math.round(d.left / main.clientWidth), dir = dx < -40 ? 1 : dx > 40 ? -1 : 0, t = self.slideAt(from + dir);
        if (t) self.show(t.getAttribute('data-media-id'), true);
      };
      main.addEventListener('pointerup', endDrag); main.addEventListener('pointercancel', endDrag); main.addEventListener('pointerleave', endDrag);
      main.setAttribute('tabindex', '0');
      main.addEventListener('keydown', function (e) { if (e.key === 'ArrowRight') { e.preventDefault(); self.step(1); } else if (e.key === 'ArrowLeft') { e.preventDefault(); self.step(-1); } });
      this.addEventListener('click', function (e) {
        var th = e.target.closest('[data-v3-thumb]');
        if (th) self.show(th.getAttribute('data-v3-thumb'), true);
      });
      /* desktop card layout: a wheel over the hero scrolls the buy card beside it, so the column can be read
         without aiming the pointer at it; at the card's end (or its top, wheeling up) the wheel falls through
         to the page as before (18 Sep 2026 review) */
      this.addEventListener('wheel', function (e) {
        if (mobile.matches || e.ctrlKey || !e.deltaY || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; /* pinch zoom and sideways scrolls stay with the carousel */
        var grid = self.closest('.v3-pdp__grid');
        if (!grid || grid.classList.contains('v3-pdp__grid--flow')) return; /* the open column is pure position sticky, nothing to hand off to */
        var card = grid.querySelector('.v3-pdp__buy--card'); if (!card) return;
        var d = e.deltaY;
        if (e.deltaMode === 1) d *= 16; else if (e.deltaMode === 2) d *= card.clientHeight; /* lines and pages to pixels */
        var room = d > 0 ? card.scrollHeight - card.clientHeight - card.scrollTop : card.scrollTop;
        if (room <= 1) return;
        e.preventDefault();
        card.scrollTop += d; /* direct, never scrollTo: Safari trackpads fire a stream of small deltas and a smooth scroll would fight them */
      }, { passive: false });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.isIntersecting) self.show(en.target.getAttribute('data-media-id'), false); });
        }, { root: main, threshold: 0.6 });
        main.querySelectorAll('.v3-gallery__slide').forEach(function (s) { self.io.observe(s); });
      }
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
    slides() { return this.main ? this.main.querySelectorAll('.v3-gallery__slide') : []; }
    slideAt(i) { var s = this.slides(); return s[Math.max(0, Math.min(s.length - 1, i))] || null; }
    currentIndex() { var s = this.slides(), x = this.main.scrollLeft, best = 0; for (var i = 0; i < s.length; i++) if (Math.abs(s[i].offsetLeft - x) < Math.abs(s[best].offsetLeft - x)) best = i; return best; }
    step(dir) { var t = this.slideAt(this.currentIndex() + dir); if (t) this.show(t.getAttribute('data-media-id'), true); }
    /* thumbnails are plain buttons: aria-current marks the one on screen (no tab roles, there is no tabpanel) */
    show(id, scroll) {
      var main = this.main; if (!main) return;
      var slides = this.slides(), found = main.querySelector('.v3-gallery__slide[data-media-id="' + id + '"]');
      if (!found) return;
      var prog = this.querySelector('[data-v3-gallery-progress]');
      if (prog) prog.style.setProperty('--i', Array.prototype.indexOf.call(slides, found));
      var thumbs = this.querySelectorAll('[data-v3-thumb]'), active = null;
      for (var j = 0; j < thumbs.length; j++) {
        var t = thumbs[j].getAttribute('data-v3-thumb') === id;
        thumbs[j].classList.toggle('is-active', t);
        if (t) { active = thumbs[j]; thumbs[j].setAttribute('aria-current', 'true'); } else thumbs[j].removeAttribute('aria-current');
      }
      /* the thumbs are a column down the left of the hero (18 Sep 2026): with more media than fits, bring the
         active one back into the column. Only when it actually overflows, so a short list never moves the page. */
      var strip = active ? active.parentElement : null;
      if (strip && strip.scrollHeight > strip.clientHeight + 1) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (scroll) main.scrollTo({ left: found.offsetLeft, behavior: v3.reduceMotion ? 'auto' : 'smooth' });
    }
  }
  if (!customElements.get('v3-gallery')) customElements.define('v3-gallery', V3Gallery);
  function showMedia(id, scroll) { var g = doc.querySelector('v3-gallery'); if (g) g.show(id, scroll); }

  /* ---------- <v3-product-form> ------------------------------------------ */
  class V3ProductForm extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.section = this.closest('[data-v3-pdp]');
      this.formId = this.section ? this.section.getAttribute('data-form-id') : null;
      this.appManaged = this.getAttribute('data-app-managed') === 'true';
      try {
        this.variants = JSON.parse(this.querySelector('[data-v3-variants]').textContent);
        this.product = JSON.parse(this.querySelector('[data-v3-product]').textContent);
      } catch (e) { if (window.Shopify && Shopify.designMode) console.warn('v3-product-form: variant data missing', e); return; }
      this.current = this.currentVariant();
      this.initialId = this.current ? this.current.id : null;
      this.section.addEventListener('change', function (e) { self.onChange(e); });
      this.wireGift();
      this.wireRecipient();
      this.wireGate();
      this.wireSubmit();
      this.wireErrors();
      this.watchApp();
      this.render(false);
    }
    disconnectedCallback() { if (this.mo) this.mo.disconnect(); if (this.sync) this.sync.cancel(); }
    /* ---------- customise before adding (custom range, 4 Sep 2026) ----------
       With the options app on the page, the add buttons read "Customise yours" and scroll to the options until a
       sprinkle colour is picked (and, with the stricter setting, a message field has been focused and left). State comes from the app's DOM
       (.selector-wrapper groups, .bcpo-title labels, radios and text fields), so app re-renders are handled by
       re-reading it whenever the section changes. Without the app UI there is nothing to customise and the gate stays open. */
    wireGate() {
      var self = this, sec = this.section;
      this.gateOn = sec.getAttribute('data-require-custom') === 'true';
      if (!this.gateOn) return;
      this.customiseLabel = sec.getAttribute('data-customise-label') || 'Customise yours';
      this.needMessage = sec.getAttribute('data-require-message') === 'true'; /* off by default: a sprinkle pick alone opens the gate */
      this.textLeft = false;
      sec.addEventListener('focusout', function (e) { if (self.appRoot() && self.appRoot().contains(e.target) && self.isTextField(e.target)) { self.textLeft = true; self.renderGate(); } });
      sec.addEventListener('change', function (e) { if (self.appRoot() && self.appRoot().contains(e.target)) self.renderGate(); });
      sec.addEventListener('input', function (e) { if (self.appRoot() && self.appRoot().contains(e.target) && self.isTextField(e.target)) self.renderGate(); });
      var f = this.form();
      if (f) f.addEventListener('submit', function (e) { if (self.gateBlocks()) { e.preventDefault(); e.stopImmediatePropagation(); self.nudge(); } }, true);
      /* the app validates on the button before the form ever submits, so the nudge hooks the click too (capture) */
      sec.addEventListener('click', function (e) { if (e.target.closest('[data-v3-atc]') && self.gateBlocks()) { e.preventDefault(); e.stopImmediatePropagation(); self.nudge(); } }, true);
      this.renderGate();
    }
    appRoot() { return this.section.querySelector(APP_SEL); }
    isTextField(el) { return el && ((el.tagName === 'INPUT' && /^(text|search|email|tel|url)$/.test(el.type)) || el.tagName === 'TEXTAREA'); }
    sprinkleGroup() {
      var root = this.appRoot(); if (!root) return null;
      var groups = root.querySelectorAll('.selector-wrapper, [data-unique]');
      for (var i = 0; i < groups.length; i++) { var t = groups[i].querySelector('.bcpo-title, label, legend'); if (t && /sprinkle/i.test(t.textContent)) return groups[i]; }
      return null;
    }
    sprinkleDone() {
      var g = this.sprinkleGroup(); if (!g) return true; /* no sprinkle option on this product */
      if (g.querySelector('input:checked, .bcpo-active, [aria-checked="true"]')) return true;
      var sel = g.querySelector('select'); return !!(sel && sel.value && sel.selectedIndex > 0);
    }
    textDone() {
      var root = this.appRoot(); if (!root) return true;
      var fields = Array.prototype.filter.call(root.querySelectorAll('input, textarea'), this.isTextField).filter(function (f) { return f.offsetParent !== null; });
      if (!fields.length) return true; /* no message fields for this size */
      if (this.textLeft) return true;
      return fields.some(function (f) { return f.value.trim().length > 0; });
    }
    gateBlocks() { return this.gateOn && !!(this.current && this.current.available) && !!this.appRoot() && !(this.sprinkleDone() && (!this.needMessage || this.textDone())); } /* sold out stays sold out */
    renderGate() {
      if (!this.gateOn) return;
      var blocked = this.gateBlocks(), sec = this.section, label = this.customiseLabel;
      var main = sec.querySelector('[data-v3-atc-label]');
      if (main) { if (blocked) setText(main, label); else if (this.current && this.current.available) setText(main, main.getAttribute('data-label')); }
      var sep = sec.querySelector('.v3-pdp__atc-sep'), price = sec.querySelector('[data-v3-atc-price]');
      if (sep && sep.hidden !== blocked) sep.hidden = blocked;
      if (price && price.hidden !== blocked) price.hidden = blocked;
      doc.querySelectorAll('[data-v3-sticky-add] .v3-btn__label, [data-v3-remote-add] .v3-btn__label').forEach(function (l) {
        if (!l.hasAttribute('data-add-label')) l.setAttribute('data-add-label', l.textContent);
        setText(l, blocked ? label : l.getAttribute('data-add-label'));
      });
    }
    nudge() {
      var target = this.sprinkleDone() ? null : this.sprinkleGroup();
      if (!target) { var root = this.appRoot(); var f = root && Array.prototype.find.call(root.querySelectorAll('input, textarea'), this.isTextField); target = (f && f.closest('.selector-wrapper')) || f || root; }
      if (!target) return;
      target.scrollIntoView({ block: 'center', behavior: v3.reduceMotion ? 'auto' : 'smooth' });
      target.classList.add('is-nudged'); setTimeout(function () { target.classList.remove('is-nudged'); }, 1400);
    }
    form() { return this.formId ? doc.getElementById(this.formId) : null; }
    /* the hidden id input (multi option and app managed products); null when the radios carry the id */
    idInput() { var f = this.form(); return f ? f.querySelector('input[type="hidden"][name="id"]') : null; }
    /* the variant id the form would submit right now: checked radio, select or hidden input named id */
    chosenId() {
      var f = this.form(); if (!f) return null;
      var el = Array.prototype.find.call(f.elements, function (c) { return c.name === 'id' && (c.type === 'hidden' || c.tagName === 'SELECT' || c.checked); });
      return el ? el.value : null;
    }
    byId(id) { id = Number(id); return this.variants.find(function (v) { return v.id === id; }) || null; }
    /* VO Product Options replaces the native picker with its own UI; hide our anchor and start reading its id.
       The app writes the id in response to the customer's clicks and re-renders its own markup, so a section level
       listener for those events plus a MutationObserver (kept open) replace the old 250ms poll. */
    watchApp() {
      if (!this.appManaged) return;
      var self = this, sec = this.section, native = sec.querySelector('[data-v3-native-picker]');
      this.sync = v3.debounce(function () { self.syncApp(); }, 40);
      var later = function () { self.sync(); setTimeout(function () { self.syncApp(); }, 350); }; /* the app may finish its own handler after ours */
      var arm = function () {
        if (native) native.classList.add('is-replaced');
        self.placeApp();
        self.wireAppCart();
        self.lastId = self.chosenId();
        ['change', 'input', 'click'].forEach(function (t) { sec.addEventListener(t, later, true); });
      };
      this.mo = new MutationObserver(function () {
        if (self.armed) { self.sync(); return; }
        if (self.appRoot()) { self.armed = true; arm(); }
      });
      this.mo.observe(sec, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] }); /* kept open: the app can take well over 8s to render on slow connections, and re-renders later */
      if (this.appRoot()) { this.armed = true; arm(); }
    }
    syncApp() {
      this.placeApp(); /* the app re-inserts its UI on some re-renders; keep it in the picker slot */
      if (this.gateOn) this.renderGate();
      var id = this.chosenId(); if (id == null) return;
      if (id !== this.lastId) { this.lastId = id; this.current = this.currentVariant(); this.render(true); }
    }
    /* The app prepends its UI (#bcpo) to the form, above the title. Move it into the tier picker's slot so the
       options sit where the native picker would: under the lede, above delivery and the gift note (4 Sep 2026). */
    placeApp() {
      var picker = this.section.querySelector('[data-v3-picker]'), app = this.section.querySelector(APP_SEL), f = this.form();
      if (!picker || !app || !f || picker.contains(app)) return;
      var root = app;
      while (root.parentElement && root.parentElement !== f) root = root.parentElement;
      if (root.parentElement === f) picker.appendChild(root);
    }
    /* one value per option position; a group whose inputs are named id maps the chosen variant back to its option */
    selectedOptions() {
      var vals = [], self = this;
      var groups = this.section.querySelectorAll('[data-v3-option]');
      for (var i = 0; i < groups.length; i++) {
        var g = groups[i], pos = Number(g.getAttribute('data-v3-option')), input = g, v = null;
        if (g.tagName !== 'SELECT' && g.tagName !== 'INPUT') input = g.querySelector('input:checked');
        if (input) {
          if (input.name === 'id') { var match = self.byId(input.value); v = match ? match.options[pos - 1] : null; }
          else v = input.value;
        }
        vals[pos - 1] = v;
      }
      return vals;
    }
    findVariant(opts, overridePos, overrideVal) {
      return this.variants.find(function (v) {
        return v.options.every(function (o, idx) { return idx === overridePos ? o === overrideVal : (opts[idx] == null || o === opts[idx]); });
      }) || null;
    }
    currentVariant() {
      var id = this.chosenId(), byId = id == null ? null : this.byId(id);
      if (this.appManaged && !!this.appRoot()) return byId;
      var opts = this.selectedOptions();
      if (!opts.length) return byId || this.variants[0];
      return this.findVariant(opts);
    }
    onChange(e) {
      if (!e.target.closest('[data-v3-option], [data-v3-picker]')) return;
      if (this.appManaged && !!this.appRoot()) { this.syncApp(); return; }
      this.current = this.findVariant(this.selectedOptions());
      var idIn = this.idInput();
      if (this.current && idIn) idIn.value = this.current.id;
      if (this.current) this.lastId = String(this.current.id);
      this.render(true);
    }
    /* brownies per variant, resolved once in Liquid (snippets/v3-variant-count.liquid) */
    unitCount(v) { return v ? Number(v.count) || 0 : 0; }
    render(userAction) {
      this.renderPicker();
      this.renderBuy();
      this.renderMirror(userAction);
    }
    renderPicker() {
      var sec = this.section, self = this;
      var labels = sec.querySelectorAll('.v3-tier, .v3-pill');
      for (var i = 0; i < labels.length; i++) { var inp = labels[i].querySelector('input'); labels[i].classList.toggle('is-selected', !!(inp && inp.checked)); }
      var cardsGroup = sec.querySelector('.v3-tiers__group--cards'); if (!cardsGroup) return;
      var opts = this.selectedOptions(), pos = Number(cardsGroup.getAttribute('data-v3-option')) - 1, fmt = this.product.money_format;
      cardsGroup.querySelectorAll('.v3-tier').forEach(function (card) {
        var input = card.querySelector('input'), match = input.name === 'id' ? self.byId(input.value) : self.findVariant(opts, pos, input.value);
        if (!match) return;
        setText(card.querySelector('[data-v3-tier-price]'), formatMoney(match.price, fmt));
        var u = card.querySelector('[data-v3-tier-unit]'), count = self.unitCount(match);
        if (u && count > 0) setText(u, formatMoney(Math.floor(match.price / count), fmt) + '/' + (u.getAttribute('data-unit') || 'each'));
        card.classList.toggle('is-unavailable', !match.available);
        input.setAttribute('aria-disabled', match.available ? 'false' : 'true');
      });
    }
    renderBuy() {
      var v = this.current, sec = this.section, fmt = this.product.money_format;
      var atc = sec.querySelector('[data-v3-atc]'), label = sec.querySelector('[data-v3-atc-label]'), price = sec.querySelector('[data-v3-atc-price]');
      var compare = sec.querySelector('[data-v3-compare]'); /* struck through compare-at inside the button; the caption line under it went on 16 Sep 2026 */
      var available = !!(v && v.available);
      if (atc) atc.disabled = !available;
      doc.querySelectorAll('[data-v3-sticky-add], [data-v3-remote-add]').forEach(function (b) { b.disabled = !available; });
      if (label) setText(label, available ? label.getAttribute('data-label') : (v ? label.getAttribute('data-sold-out') : label.getAttribute('data-unavailable')));
      if (this.gateOn) this.renderGate();
      if (price) setText(price, v ? formatMoney(v.price, fmt) : '');
      if (compare) { var onSale = v && v.compare_at_price && v.compare_at_price > v.price; compare.hidden = !onSale; if (onSale) setText(compare, formatMoney(v.compare_at_price, fmt)); }
    }
    renderMirror(userAction) {
      var v = this.current; if (!v) return;
      var fmt = this.product.money_format;
      var sp = doc.querySelector('[data-v3-sticky-price]'), sv = doc.querySelector('[data-v3-sticky-variant]');
      if (sp) setText(sp, formatMoney(v.price, fmt));
      if (sv && this.variants.length > 1) { var sf = sv.getAttribute('data-format'), sc = this.unitCount(v); setText(sv, sf && sc > 0 ? sf.replace('[count]', sc) : v.title); }
      /* the address bar always mirrors the choice: ?variant= for any other variant, no parameter for the initial one */
      if (userAction && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        if (v.id === this.initialId) url.searchParams.delete('variant'); else url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (userAction && v.featured_media && v.featured_media.id) showMedia(String(v.featured_media.id), true);
      this.section.dispatchEvent(new CustomEvent('v3:variant', { bubbles: true, detail: { variant: v } }));
    }
    wireGift() {
      var sec = this.section, toggle = this.giftToggle = sec.querySelector('[data-v3-gift-toggle]'), field = sec.querySelector('[data-v3-gift-field]'), note = this.giftNote = sec.querySelector('[data-v3-gift-note]'), count = sec.querySelector('[data-v3-gift-count]');
      if (!toggle) return;
      this.savedNote = toggle.checked && note ? note.value : ''; /* what the cart holds now (Liquid rendered the cart note into the field) */
      toggle.addEventListener('change', function () { field.hidden = !toggle.checked; if (toggle.checked && note) note.focus(); });
      if (note) note.addEventListener('input', function () { if (count) count.textContent = note.value.length; });
    }
    /* Gift card recipient (blocks/v3-product-gift-card-recipient): the fields stay disabled, so FormData leaves them
       out, until "send it to them" is ticked. Shopify then emails the card to the recipient once the order is paid. */
    wireRecipient() {
      var sec = this.section, toggle = sec.querySelector('[data-v3-recipient-toggle]'), box = sec.querySelector('[data-v3-recipient-fields]');
      if (!toggle || !box) return;
      var inputs = box.querySelectorAll('input, textarea');
      function sync() {
        box.hidden = !toggle.checked;
        inputs.forEach(function (i) { i.disabled = !toggle.checked; });
      }
      toggle.addEventListener('change', function () { sync(); if (toggle.checked) { var first = box.querySelector('input[type="email"]'); if (first) first.focus(); } });
      sync();
    }
    /* Save the gift note as the cart note (the order's Notes box) after an add. Resolves true when it posted (and so re-rendered the drawer),
       false when there was nothing new to save: no note block, or the note already matches what the cart holds. */
    saveGift() {
      var self = this, toggle = this.giftToggle, note = this.giftNote;
      if (!toggle || !note || !v3.cart) return Promise.resolve(false);
      var want = toggle.checked ? note.value : '';
      if (want === this.savedNote) return Promise.resolve(false);
      return v3.cart.updateNote(want).then(function () { self.savedNote = want; return true; }, function () { return false; });
    }
    /* after any successful add (native form or the app): clear the error line, then the gift note */
    onAdded() {
      var box = this.section.querySelector('[data-v3-form-error]'); if (box) box.hidden = true;
      return this.saveGift();
    }
    /* Remote add buttons (sticky bar, monthly mix band) act as a tap on the real add button, queried live because the
       options app clones it. Every add then runs one path: the app's validation and add when it owns the button, else
       a native submission that v3-cart.js intercepts. Never form.submit(): it fires no submit event, so nothing could
       intercept it and the customer landed on /cart (16 Sep 2026 review; phones on Safari before 16 took this path). */
    remoteAdd() {
      var f = this.form(), sec = this.section; if (!f) return;
      if (this.gateBlocks()) { this.nudge(); return; }
      this.wireAppCart();
      var atc = sec.querySelector('[data-v3-atc]');
      if (atc && !atc.disabled) { atc.click(); return; }
      if (f.requestSubmit) { f.requestSubmit(); return; }
      if (f.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))) f.submit(); /* nothing intercepted the add */
    }
    wireSubmit() {
      var self = this, sec = this.section; if (!this.form()) return;
      /* the app binds its click handler straight on the button, so hook its after-add event before that runs */
      sec.addEventListener('click', function (e) { if (e.target.closest('[data-v3-atc]')) self.wireAppCart(); }, true);
    }
    /* The options app (VO Product Options, window.bcpo) owns the add button when it manages the product: its click
       handler stops the event before the form can submit, validates its options, posts /cart/add.js itself and then
       sends the customer to /cart (its "override ajax" setting, on every device). The app skips that redirect when a
       bcpo:afterAdd handler returns true, so the bag drawer opens instead (16 Sep 2026 review). Bound through the app's
       own jQuery: triggerHandler only reaches handlers registered on that instance. No drawer on the page: the app
       keeps its redirect. Hooked once per page; the handler finds the live form element, so a re-rendered section
       is fine. One drawer render: the gift note update when there is one to save, else a refresh. */
    wireAppCart() {
      var app = window.bcpo, $ = app && app.$; if (!$ || !$.fn || app.v3BagHooked) return;
      app.v3BagHooked = true;
      $(doc).on('bcpo:afterAdd', function () {
        if (!v3.cart) return;
        var pf = productForm(), sec = pf && pf.section;
        var atc = sec && sec.querySelector('[data-v3-atc]');
        if (atc) { atc.disabled = false; atc.style.paddingRight = ''; atc.style.position = ''; } /* the app disables the button and pads it for its loader */
        $('.r-apps-loader').addClass('r-apps-loader--hidden');
        var open = function () { v3.cart.open(); };
        (pf ? pf.onAdded() : Promise.resolve(false))
          .then(function (rendered) { return rendered ? null : (v3.cart.refresh ? v3.cart.refresh() : null); })
          .then(open, open);
        return true;
      });
    }
    wireErrors() {
      var self = this, f = this.form(), sec = this.section; if (!f) return;
      f.addEventListener('v3:cart:error', function (e) {
        var box = sec.querySelector('[data-v3-form-error]'); if (!box) return;
        box.textContent = (e.detail && e.detail.message) || box.getAttribute('data-generic'); box.hidden = false;
      });
      f.addEventListener('v3:cart:added', function () { self.onAdded(); }); /* v3-cart.js already rendered the add; the note, if new, renders once more */
    }
  }
  if (!customElements.get('v3-product-form')) customElements.define('v3-product-form', V3ProductForm);
  /* one delegated listener for every remote add button, so a re-rendered product section never double binds */
  doc.addEventListener('click', function (e) {
    var s = e.target.closest('[data-v3-sticky-signup]');
    if (s) {
      /* coming soon: bring the hear first signup into view (inside the buy card on desktop) and put the cursor in it */
      var w = doc.querySelector('[data-v3-waitlist]'); if (!w) return;
      w.scrollIntoView({ block: 'center', behavior: v3.reduceMotion ? 'auto' : 'smooth' });
      var field = w.querySelector('input[type="email"]'); if (field) field.focus({ preventScroll: true });
      return;
    }
    var b = e.target.closest('[data-v3-sticky-add], [data-v3-remote-add]'); if (!b || b.disabled) return;
    var pf = productForm(); if (pf && pf.remoteAdd) pf.remoteAdd();
  });

  /* ---------- <v3-sticky-bar>: floating add to bag (kit "sticky bar") ----------
     desktop: only once the buy row has scrolled above the viewport (the mockup's ctaRef.bottom < 0);
     mobile: whenever the buy row is off screen. Never over the footer. The buy row lives in another section, so it is
     looked up again whenever the cached node has left the document (editor re-render). In coming soon mode
     (data-v3-sticky-waitlist) the hear first signup stands in for the buy row, and the bar stays hidden once the
     signup has gone through (its success line, data-v3-waitlist-done, is showing). */
  class V3StickyBar extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.footerVisible = false;
      this.onScroll = function () { self.update(); };
      this.onResize = function () { self.measure(); self.update(); };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      if (mobile.addEventListener) mobile.addEventListener('change', this.onResize);
      var footer = doc.querySelector('.v3-footer');
      if (footer && 'IntersectionObserver' in window) { this.io = new IntersectionObserver(function (en) { self.footerVisible = en[0].isIntersecting; self.update(); }, { threshold: 0 }); this.io.observe(footer); }
      this.measure();
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll); window.removeEventListener('resize', this.onResize);
      if (mobile.removeEventListener) mobile.removeEventListener('change', this.onResize);
      if (this.io) this.io.disconnect();
      if (this.card) this.card.removeEventListener('scroll', this.onScroll);
    }
    /* find the buy row and its card, and read the card's overflow once (not on every scroll) */
    measure() {
      this.waitlist = this.hasAttribute('data-v3-sticky-waitlist');
      var buy = doc.querySelector(this.waitlist ? '[data-v3-waitlist]' : '[data-v3-buy]');
      if (buy !== this.buy) {
        if (this.card) this.card.removeEventListener('scroll', this.onScroll);
        this.buy = buy;
        this.card = buy ? buy.closest('.v3-pdp__buy') : null;
        if (this.card) this.card.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.cardScrolls = !!(this.card && getComputedStyle(this.card).overflowY === 'auto');
    }
    /* the buy row counts as "above" once it has scrolled past the top of whatever clips it: the buy card on
       desktop (its own scroller), the viewport on phones */
    buyState() {
      var r = this.buy.getBoundingClientRect(), top = 0, bottom = window.innerHeight;
      if (this.cardScrolls) { var c = this.card.getBoundingClientRect(); top = Math.max(top, c.top); bottom = Math.min(bottom, c.bottom); }
      if (r.bottom <= top) return 'above';
      if (r.top >= bottom) return 'below';
      return 'visible';
    }
    /* two rect reads per scroll event: cheap enough to run directly (no rAF, which hidden tabs never fire) */
    update() {
      if (!this.buy || !this.buy.isConnected) this.measure();
      if (!this.buy || (this.waitlist && this.buy.querySelector('[data-v3-waitlist-done]:not([hidden])'))) { this.hidden = true; return; }
      var state = this.buyState(), away = mobile.matches ? state !== 'visible' : state === 'above';
      var hide = !away || this.footerVisible;
      if (this.hidden !== hide) this.hidden = hide;
    }
  }
  if (!customElements.get('v3-sticky-bar')) customElements.define('v3-sticky-bar', V3StickyBar);

  /* ---------- pairs well with (Product Recommendations API) --------------- */
  var pairs = doc.querySelector('[data-v3-pairs]');
  function loadPairs(url, retry) {
    v3.fetchFragment(url, '[data-v3-pairs-result]').then(function (res) {
      if (res && res.innerHTML.trim()) pairs.innerHTML = res.innerHTML;
      else if (retry) loadPairs(url.replace('intent=complementary', 'intent=related'), false);
    }).catch(function () {});
  }
  if (pairs && !pairs.children.length && pairs.getAttribute('data-url')) loadPairs(pairs.getAttribute('data-url'), true);

  /* ---------- <v3-countdown> ---------------------------------------------- */
  class V3Countdown extends HTMLElement {
    connectedCallback() {
      var c = (this.getAttribute('data-cutoff') || '12:00').split(':');
      this.cutoff = [Number(c[0]) || 0, Number(c[1]) || 0]; /* "12" (free text setting) reads as 12:00, never NaN */
      this.tz = this.getAttribute('data-tz') || 'Europe/London';
      this.days = (this.getAttribute('data-days') || '1,2,3,4,5').split(',').map(Number).filter(function (d) { return d >= 1 && d <= 7; });
      this.before = this.getAttribute('data-before') || 'Order in {time} for dispatch today';
      this.after = this.getAttribute('data-after') || 'Next dispatch {day}';
      this.tomorrow = this.getAttribute('data-tomorrow') || 'tomorrow';
      this.locale = doc.documentElement.lang || 'en';
      if (!this.days.length) return;
      var self = this; this.tick(); this.timer = setInterval(function () { self.tick(); }, 30000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    /* an unknown time zone in the setting throws inside Intl: fall back to the device clock rather than break every tick */
    fmt(opts) {
      try { return new Intl.DateTimeFormat('en-GB', Object.assign({ timeZone: this.tz }, opts)); }
      catch (e) { return new Intl.DateTimeFormat('en-GB', opts); }
    }
    nowInTz() {
      var parts = this.fmt({ weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(new Date());
      var get = function (t) { var p = parts.find(function (x) { return x.type === t; }); return p ? p.value : ''; };
      var wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
      return { day: wd === 0 ? 7 : wd, h: Number(get('hour')) % 24, m: Number(get('minute')) };
    }
    /* the name of the day `steps` days from now, in the page language */
    dayName(steps) {
      var d = new Date(Date.now() + steps * 86400000), opts = { weekday: 'long' };
      try { return new Intl.DateTimeFormat(this.locale, Object.assign({ timeZone: this.tz }, opts)).format(d); }
      catch (e) { try { return new Intl.DateTimeFormat(this.locale, opts).format(d); } catch (e2) { return new Intl.DateTimeFormat('en', opts).format(d); } }
    }
    tick() {
      var n = this.nowInTz(), isDispatch = this.days.indexOf(n.day) !== -1;
      var minsLeft = (this.cutoff[0] * 60 + this.cutoff[1]) - (n.h * 60 + n.m);
      if (isDispatch && minsLeft > 0) {
        var h = Math.floor(minsLeft / 60), m = minsLeft % 60;
        setText(this, this.before.replace('{time}', (h ? h + 'h ' : '') + m + 'm'));
      } else {
        var d = n.day, steps = 0;
        do { d = d % 7 + 1; steps++; } while (this.days.indexOf(d) === -1 && steps < 8);
        setText(this, this.after.replace('{day}', steps === 1 ? this.tomorrow : this.dayName(steps)));
      }
    }
  }
  if (!customElements.get('v3-countdown')) customElements.define('v3-countdown', V3Countdown);
})();
